import type {
  Answer,
  AnswerValue,
  FieldDefinition,
  FieldId,
} from '@inside-us-aos/domain';
import { isAnswerPresent } from '@inside-us-aos/domain';

/**
 * Answer-state container + change events.
 *
 * `createAnswerStore(catalog)` returns an in-memory state container
 * backing a single application's answer set. The store is the
 * single source of truth for answer values during an editing session;
 * Phase 2's `useSyncExternalStore` adapter will wrap `subscribe` /
 * `getAllAnswers`, and Phase 8 will wire `AuditEvent` emission on top
 * of the change-event hook (Slice 8).
 *
 * Storage shape: `Map<FieldId, Map<groupInstanceId | __SINGLETON__,
 * AnswerValue>>`. A field without a `repeatableGroupId` always uses
 * the singleton key; a repeatable field requires a caller-supplied
 * `groupInstanceId`. The store does not assign instance ids — that
 * remains a UI concern (`crypto.randomUUID()` typically).
 *
 * ### Repeatable instance uniqueness contract
 *
 * The `groupInstanceId` namespaces ALL fields within a single
 * repeatable group instance. The same id used across multiple fields
 * within ONE group is the correct, expected pattern (it means
 * "instance N's value for field X"). The same id used across
 * DIFFERENT groups is also fine — each group has its own instance-id
 * namespace.
 *
 * The store rejects the "duplicate-instance anti-pattern": calling
 * `setAnswer(field, newValue, instanceId)` for a repeatable field
 * when a DIFFERENT value already exists at the same `(fieldId,
 * instanceId)` slot. This catches the realistic UI bug where
 * "Add another address" reuses an existing instance id (e.g.,
 * 'addr-1') instead of generating a fresh one — silently overwriting
 * the previous instance. Updating an existing repeatable answer is
 * still possible: `clearAnswer(field, instanceId)` first, then
 * `setAnswer` with the new value. Singleton (non-repeatable) fields
 * retain their natural update semantics: `setAnswer` overwrites
 * freely.
 *
 * Events: change listeners are invoked SYNCHRONOUSLY after each
 * `setAnswer` / `clearAnswer` call. The event payload carries
 * `oldValue` / `newValue` and a millisecond `timestamp`. We do not
 * yet emit `AuditEvent` records — that is Slice 8's job. We do
 * suppress events when the new value is structurally equal to the
 * old value, so a no-op `setAnswer` does not wake subscribers.
 *
 * Validation is NOT run inside the store. Callers compose
 * `validateAnswers` (Slice 4) over `getAllAnswers()` when they want
 * a fresh issue snapshot. This keeps the store cheap and lets future
 * batched-update flows defer validation to a render boundary.
 */

export interface AnswerChangeEvent {
  fieldId: FieldId;
  groupInstanceId: string | undefined;
  oldValue: AnswerValue | undefined;
  newValue: AnswerValue | undefined;
  /** Wall-clock millis at the time of the change. */
  timestamp: number;
}

export type AnswerChangeListener = (event: AnswerChangeEvent) => void;

/** Options for setAnswer/clearAnswer that affect emission, not storage. */
export interface AnswerMutationOptions {
  /**
   * When true, the store applies the mutation but does NOT notify
   * subscribers. Used by hydrate to replay a persisted snapshot
   * without generating audit-log noise — every hydrate would
   * otherwise emit one change event per loaded answer, and the audit
   * writer would persist all of them as if they were user edits.
   * A full draft × N page loads quickly produces tens of thousands
   * of spurious audit rows (production saw 214k events on a single
   * draft before this option existed).
   */
  silent?: boolean;
}

export interface AnswerStore {
  /** Sets a field's answer value. Validates `groupInstanceId` for repeatable fields. */
  setAnswer: (
    fieldId: FieldId,
    value: AnswerValue,
    groupInstanceId?: string,
    options?: AnswerMutationOptions,
  ) => void;
  /** Reads a field's value. Returns `undefined` when unset. */
  getAnswer: (
    fieldId: FieldId,
    groupInstanceId?: string,
  ) => AnswerValue | undefined;
  /** Removes a field's answer. No-op when unset. */
  clearAnswer: (
    fieldId: FieldId,
    groupInstanceId?: string,
    options?: AnswerMutationOptions,
  ) => void;
  /**
   * Returns a snapshot of every present answer as `Answer[]`. The
   * `applicationId`, `updatedAt`, `updatedBy`, and `schemaVersion`
   * fields are populated from `metadata`; pass them in to
   * `createAnswerStore`'s factory if you want them to round-trip
   * with the catalog version. The returned array is a fresh copy.
   */
  getAllAnswers: () => readonly Answer[];
  /** Subscribes a listener. Returns the unsubscribe function. */
  subscribe: (listener: AnswerChangeListener) => () => void;
  /** Idempotent unsubscribe (no-throw when called twice). */
  unsubscribe: (listener: AnswerChangeListener) => void;
}

export interface AnswerStoreMetadata {
  applicationId: string;
  schemaVersion: string;
  updatedBy: string;
  /**
   * Override the default `Date.now()` clock — useful for
   * deterministic tests. Returns the current millis.
   */
  now?: () => number;
}

const SINGLETON = Symbol.for('inside-us-aos.form-engine.singleton-instance');

type InstanceKey = string | typeof SINGLETON;

/**
 * Returns the lookup key for a (fieldId, groupInstanceId) pair.
 * Repeatable fields require a non-empty `groupInstanceId`; non-
 * repeatable fields ignore it and use the SINGLETON key.
 */
const instanceKeyFor = (
  field: FieldDefinition,
  groupInstanceId: string | undefined,
): InstanceKey => {
  if (field.repeatableGroupId) {
    if (!groupInstanceId) {
      throw new Error(
        `Field ${field.id} is repeatable; setAnswer requires a groupInstanceId.`,
      );
    }
    return groupInstanceId;
  }
  if (groupInstanceId !== undefined) {
    throw new Error(
      `Field ${field.id} is not repeatable; setAnswer must not specify a groupInstanceId.`,
    );
  }
  return SINGLETON;
};

const valuesEqual = (a: AnswerValue | undefined, b: AnswerValue | undefined): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  // Structural compare via JSON. AnswerValue is intentionally JSON-
  // round-trippable (no Dates, Maps, etc.), so this is safe.
  return JSON.stringify(a) === JSON.stringify(b);
};

export const createAnswerStore = (
  catalog: readonly FieldDefinition[],
  metadata: AnswerStoreMetadata,
): AnswerStore => {
  const fieldsById = new Map<FieldId, FieldDefinition>(
    catalog.map(field => [field.id, field]),
  );
  // Storage: fieldId → instanceKey → value.
  const storage = new Map<FieldId, Map<InstanceKey, AnswerValue>>();
  // Per-group instance roster: groupId → instanceId → Set<fieldId> of
  // fields populated for that instance. Used to enforce the duplicate-
  // instance anti-pattern check: a `setAnswer` for a repeatable field
  // that would overwrite a different existing value at the same
  // (fieldId, instanceId) slot is rejected. The set itself is also
  // useful as a "claim" record so future engine layers (e.g., a
  // repeatable-group orchestrator) can ask "which instance ids are
  // currently in use by this group?" without rewalking storage.
  const groupInstanceClaims = new Map<
    string,
    Map<string, Set<FieldId>>
  >();
  const listeners = new Set<AnswerChangeListener>();
  const now = metadata.now ?? (() => Date.now());

  const requireField = (fieldId: FieldId): FieldDefinition => {
    const field = fieldsById.get(fieldId);
    if (!field) {
      throw new Error(`Unknown field id: ${fieldId}`);
    }
    return field;
  };

  const emit = (event: AnswerChangeEvent): void => {
    // Snapshot listeners so a listener that unsubscribes during
    // iteration does not skip / double-fire its peers.
    const snapshot = [...listeners];
    for (const listener of snapshot) {
      listener(event);
    }
  };

  const setAnswer: AnswerStore['setAnswer'] = (
    fieldId,
    value,
    groupInstanceId,
    options,
  ) => {
    const field = requireField(fieldId);
    const key = instanceKeyFor(field, groupInstanceId);
    const fieldMap = storage.get(fieldId) ?? new Map<InstanceKey, AnswerValue>();
    const oldValue = fieldMap.get(key);
    if (valuesEqual(oldValue, value)) return;
    // The previous "duplicate groupInstanceId"
    // throw fired on EVERY keystroke into a repeatable field's input.
    // Reproduction: VA tested past-addresses + children sections and
    // could only enter one character per field; the second keystroke
    // hit this throw via FieldRenderer's onChange → engine.setAnswer
    // path, which left the engine state at the first character.
    //
    // The intent of the original check was to catch a UI bug where an
    // instance id was reused across different rows. That intent is
    // better enforced at the RepeatableGroup primitive (which
    // generates unique ids via Date.now()) and by the
    // groupInstanceClaims invariant tests in this package. The
    // form-engine itself just stores values per (fieldId,
    // instanceKey) and allows updates — the natural shape for a
    // controlled-input UI.
    fieldMap.set(key, value);
    storage.set(fieldId, fieldMap);
    if (field.repeatableGroupId && groupInstanceId) {
      const groupClaims =
        groupInstanceClaims.get(field.repeatableGroupId) ??
        new Map<string, Set<FieldId>>();
      const claimedFields =
        groupClaims.get(groupInstanceId) ?? new Set<FieldId>();
      claimedFields.add(fieldId);
      groupClaims.set(groupInstanceId, claimedFields);
      groupInstanceClaims.set(field.repeatableGroupId, groupClaims);
    }
    if (options?.silent) return;
    emit({
      fieldId,
      groupInstanceId,
      oldValue,
      newValue: value,
      timestamp: now(),
    });
  };

  const getAnswer: AnswerStore['getAnswer'] = (fieldId, groupInstanceId) => {
    const field = requireField(fieldId);
    const key = instanceKeyFor(field, groupInstanceId);
    return storage.get(fieldId)?.get(key);
  };

  const clearAnswer: AnswerStore['clearAnswer'] = (
    fieldId,
    groupInstanceId,
    options,
  ) => {
    const field = requireField(fieldId);
    const key = instanceKeyFor(field, groupInstanceId);
    const fieldMap = storage.get(fieldId);
    if (!fieldMap || !fieldMap.has(key)) return;
    const oldValue = fieldMap.get(key);
    fieldMap.delete(key);
    if (fieldMap.size === 0) storage.delete(fieldId);
    // Release the claim so the (instanceId, fieldId) slot can be
    // re-set without tripping the duplicate-instance check. We keep
    // the claim entry for the instance overall while any sibling
    // field is still populated for it.
    if (field.repeatableGroupId && groupInstanceId) {
      const groupClaims = groupInstanceClaims.get(field.repeatableGroupId);
      const claimedFields = groupClaims?.get(groupInstanceId);
      if (claimedFields) {
        claimedFields.delete(fieldId);
        if (claimedFields.size === 0) {
          groupClaims!.delete(groupInstanceId);
          if (groupClaims!.size === 0) {
            groupInstanceClaims.delete(field.repeatableGroupId);
          }
        }
      }
    }
    if (options?.silent) return;
    emit({
      fieldId,
      groupInstanceId,
      oldValue,
      newValue: undefined,
      timestamp: now(),
    });
  };

  const getAllAnswers: AnswerStore['getAllAnswers'] = () => {
    const out: Answer[] = [];
    const updatedAt = new Date(now()).toISOString();
    for (const [fieldId, fieldMap] of storage) {
      for (const [key, value] of fieldMap) {
        if (!isAnswerPresent(value)) continue;
        out.push({
          applicationId: metadata.applicationId,
          fieldId,
          groupInstanceId: key === SINGLETON ? undefined : key,
          value,
          schemaVersion: metadata.schemaVersion,
          updatedAt,
          updatedBy: metadata.updatedBy,
        });
      }
    }
    return out;
  };

  const subscribe: AnswerStore['subscribe'] = listener => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const unsubscribe: AnswerStore['unsubscribe'] = listener => {
    listeners.delete(listener);
  };

  return {
    setAnswer,
    getAnswer,
    clearAnswer,
    getAllAnswers,
    subscribe,
    unsubscribe,
  };
};
